import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    getTypeColors
} from "../../../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../../../types";
import type {
    SidebarMeasures
} from "../../type";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    webStyle
} from "../../../../utils";

export const SIDEBAR_SUB_BUTTON_VARIANT: keyof NCoreUIKit.Typography = "bodyMediumSize";

export const getSidebarSubButtonColors = ({
    isEmphasized,
    isDisabled,
    isActive,
    colors
}: {
    colors: NCoreUIKit.ActivePalette["colors"];
    isEmphasized: boolean;
    isDisabled: boolean;
    isActive: boolean;
}): {
    containerColor: string;
    titleColor: string;
} => {
    const currentColors = getTypeColors({
        isSelected: isActive,
        variant: "ghost",
        type: "neutral",
        isDisabled,
        colors
    });

    if(isDisabled) {
        return {
            titleColor: currentColors.titleColor,
            containerColor: "transparent"
        };
    }

    if(isActive) {
        return {
            containerColor: currentColors.containerColor,
            titleColor: currentColors.titleColor
        };
    }

    return {
        titleColor: isEmphasized ? colors.content.text.high : colors.content.text.mid,
        containerColor: "transparent"
    };
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    item: {
        flexDirection: "column",
        position: "relative",
        minWidth: 0
    },
    surface: {
        flexDirection: "row",
        alignItems: "center",
        overflow: "hidden",
        flexGrow: 1
    },
    title: {
        flexShrink: 1,
        flexGrow: 1
    }
});

export const useStyles = ({
    containerColor,
    isFocusVisible,
    currentRadius,
    shapeConfigs,
    isDisabled,
    iconSizes,
    measures,
    borders,
    colors,
    height,
    spaces
}: {
    spaces: NCoreUIKit.ActivePalette["spaces"];
    colors: NCoreUIKit.ActivePalette["colors"];
    shapeConfigs: NCoreUIKit.ShapeConfigs;
    iconSizes: NCoreUIKit.IconSizes;
    borders: NCoreUIKit.Borders;
    measures: SidebarMeasures;
    isFocusVisible: boolean;
    containerColor: string;
    currentRadius: number;
    isDisabled: boolean;
    height: number;
}) => {
    const styles = {
        surface: {
            paddingStart: iconSizes.medium / 2 + measures.gap - spaces.spacingSm,
            paddingEnd: (measures.square - iconSizes.medium) / 2,
            backgroundColor: containerColor,
            ...getCornerStyle({
                radius: currentRadius,
                shapeConfigs
            }),
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        item: {
            marginStart: measures.square / 2 + spaces.spacingSm
        },
        container: {
            minHeight: height
        } as Mutable<ViewStyle>
    };

    if(isDisabled) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    Object.assign(styles.surface, getFocusRingStyle({
        colorKey: "default",
        isFocusVisible,
        isInset: true,
        borders,
        colors
    }));

    return styles;
};
export default stylesheet;
